import { CaretDown, CircleNotch } from "@phosphor-icons/react";
import { cva, type VariantProps } from "class-variance-authority";
import clsx from "clsx";
import {
  type ButtonHTMLAttributes,
  type ForwardedRef,
  forwardRef,
  type ReactNode,
} from "react";

const selectButtonVariants = cva(
  [
    "group flex shrink-0 items-center rounded-2xl border border-black-100 bg-black-100 outline-[3px] transition-[background,border] hover:bg-black-200",
    "focus-visible:border-purple-400 focus-visible:bg-white focus-visible:outline-none",
    "disabled:cursor-not-allowed disabled:text-black-400",
    "data-[state=open]:border-purple-400 data-[state=open]:bg-white",
  ],
  {
    variants: {
      size: {
        sm: "h-8 gap-1 px-2 text-button-xs [&_svg]:size-4",
        md: "h-10 gap-1.5 px-2.5 text-button-sm [&_svg]:size-5",
        lg: "h-11 gap-2 px-3 text-button-md [&_svg]:size-5",
      },
    },
  },
);
const gaps = {
  sm: "gap-1",
  md: "gap-1.5",
  lg: "gap-2",
} as const;

interface SelectButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof selectButtonVariants> {
  hasSelection: boolean;
  hasError?: boolean;
  rightIcon?: ReactNode;
  isLoading?: boolean;
}
function SelectButton(
  {
    children,
    className,
    size = "md",
    hasError = false,
    hasSelection,
    rightIcon = null,
    isLoading = false,
    ...rest
  }: SelectButtonProps,
  ref: ForwardedRef<HTMLButtonElement>,
) {
  return (
    <button
      {...rest}
      type="button"
      ref={ref}
      className={selectButtonVariants({
        size,
        className: clsx(
          className,
          hasError
            ? "border-red-400 outline outline-red-200"
            : "hover:border-black-200",
          hasSelection ? "text-black-950" : "text-black-500",
        ),
      })}
    >
      <span className="flex-1 truncate text-left">{children}</span>
      <div className={clsx("flex items-center", gaps[size])}>
        {isLoading ? (
          <CircleNotch className="animate-spin fill-black-400" />
        ) : (
          rightIcon
        )}
        <CaretDown className="ml-auto text-black-950 transition-transform group-data-[state=open]:rotate-180" />
      </div>
    </button>
  );
}

export default forwardRef(SelectButton);
